import { useCallback, useEffect, useMemo, useState } from "react";

import { EntityType } from "@mapmycustomers/shared/enum";
import IField from "@mapmycustomers/shared/types/fieldModel/IField";
import { isDefined } from "@mapmycustomers/shared/util/assert";

import localSettings, { MAX_RECENT_FIELDS } from "@app/config/LocalSettings";
import getFieldModelByEntityType from "@app/util/fieldModel/getByEntityType";

import IFieldsUsage from "../types/IFieldsUsage";

const useFieldsUsage = (context: string, entityType: "universal" | EntityType): IFieldsUsage => {
  const [recentlyUsedFieldNames, setRecentlyUsedFieldNames] = useState<IField["name"][]>([]);
  const [frequentlyUsedFieldNames, setFrequentlyUsedFieldNames] = useState<
    Record<IField["name"], number>
  >({});

  const recentlyUsedFields = useMemo(() => {
    // We have a special handling after this line to ensure that fieldModel was really returned
    // @ts-ignore
    const fieldModel = getFieldModelByEntityType(entityType);
    if (!fieldModel) {
      return [];
    }
    return recentlyUsedFieldNames
      .map((fieldName) => fieldModel.getByName(fieldName))
      .filter(isDefined)
      .filter((field) => field.isReadable);
  }, [entityType, recentlyUsedFieldNames]);

  useEffect(() => {
    const stats = localSettings.getFieldsUsage(context, entityType);
    setRecentlyUsedFieldNames(stats.recentlyUsed);
    setFrequentlyUsedFieldNames(stats.frequencies);
  }, [context, entityType]);

  const markFieldAsUsed = useCallback(
    (field: IField) => {
      localSettings.updateFieldUsage(context, entityType, field);
      setRecentlyUsedFieldNames((fieldNames) =>
        [...fieldNames, field.name].splice(-MAX_RECENT_FIELDS)
      );
      setFrequentlyUsedFieldNames((frequencies) => ({
        ...frequencies,
        [field.name]: (frequencies[field.name] ?? 0) + 1,
      }));
    },
    [context, entityType]
  );

  return {
    frequentlyUsedFieldNames,
    markFieldAsUsed,
    recentlyUsedFieldNames,
    recentlyUsedFields,
  };
};

export default useFieldsUsage;
